import PropTypes from "prop-types";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { WORKFLOW_DRAG_MIME, WORKFLOW_PALETTE, encodeWorkflowDragPayload, workflowHasGoalNode } from "@/lib/campaignWorkflow";
import { nodeStyles } from "@/pages/outbound/WorkflowCanvas";

export function WorkflowPalette({ className, nodes = [] }) {
  const [draggingType, setDraggingType] = useState(null);
  const hasGoal = workflowHasGoalNode(nodes);

  return (
    <aside
      className={cn(
        "border-r border-slate-200 bg-white p-2.5 sm:p-3 min-w-0",
        "md:h-full md:min-h-0 md:overflow-y-auto md:overscroll-contain scrollbar-thin",
        className,
      )}
      data-testid="node-palette"
    >
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
        Add step
      </div>
      <div className="flex flex-wrap md:flex-col gap-1.5">
        {WORKFLOW_PALETTE.map((item) => {
          const Icon = nodeStyles[item.type]?.icon;
          const isDragging = draggingType === item.type;
          const goalBlocked = item.type === "goal" && hasGoal;
          return (
            <button
              type="button"
              key={item.type}
              draggable={!goalBlocked}
              disabled={goalBlocked}
              title={
                goalBlocked
                  ? "A workflow can include only one Goal node"
                  : (item.hint || item.label)
              }
              onDragStart={(event) => {
                if (goalBlocked) {
                  event.preventDefault();
                  return;
                }
                event.dataTransfer.setData(
                  WORKFLOW_DRAG_MIME,
                  encodeWorkflowDragPayload(item.type, item.label),
                );
                event.dataTransfer.effectAllowed = "move";
                setDraggingType(item.type);
              }}
              onDragEnd={() => setDraggingType(null)}
              className={cn(
                "inline-flex items-center gap-2 rounded-md border bg-white px-2.5 py-2 md:py-1.5 text-[12px] text-slate-700 md:w-full",
                "select-none transition-colors min-h-10 md:min-h-0",
                goalBlocked
                  ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed opacity-60"
                  : "cursor-grab active:cursor-grabbing border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/40",
                isDragging && !goalBlocked
                  ? "border-indigo-400 bg-indigo-50/70 opacity-60"
                  : "",
              )}
              data-testid={`palette-${item.type}`}
              data-disabled={goalBlocked ? "true" : undefined}
            >
              {Icon && <Icon size={12} className="text-slate-500 shrink-0" />}
              <span className="whitespace-nowrap flex-1 text-left">{item.label}</span>
            </button>
          );
        })}
      </div>
      <p className="mt-3 text-[10px] text-slate-400 leading-snug hidden md:block">
        Drag a step onto the canvas, then drag from a handle to connect it. Configure message content in Step details. Scroll or middle-drag to pan; Ctrl/⌘+scroll to zoom. Select a step and press Delete or click × to remove. Select a connection and click × to remove the edge.
      </p>
      <p className="mt-2 text-[10px] text-slate-400 leading-snug md:hidden">
        Drag a step onto the canvas below.
      </p>
    </aside>
  );
}

WorkflowPalette.propTypes = {
  className: PropTypes.string,
  nodes: PropTypes.arrayOf(PropTypes.object),
};
